import api from "@/services/api"
import { buildEndpoint } from "@/services/endpoints"

const ENDPOINTS = {
  STATUS: buildEndpoint("marketplace.status"),
  OZON_PRESENCE: buildEndpoint("marketplace.ozonPresence"),
  OZON_PUBLISH: buildEndpoint("marketplace.ozonPublish"),
  OZON_PHOTO: buildEndpoint("marketplace.ozonPhoto"),
  WB_CREATE: buildEndpoint("marketplace.wbCreate"),
  WB_UPDATE: buildEndpoint("marketplace.wbUpdate"),
  WB_PHOTO: buildEndpoint("marketplace.wbPhoto"),
  WB_PRESENCE: buildEndpoint("marketplace.wbPresence"),
}

export const MARKETPLACE_REQUEST_KEY_PATTERN = /^[A-Za-z0-9._:-]{32,128}$/

export function isValidMarketplaceRequestKey(requestKey) {
  return (
    typeof requestKey === "string" &&
    MARKETPLACE_REQUEST_KEY_PATTERN.test(requestKey)
  )
}

const AMBIGUOUS_MARKETPLACE_HTTP_STATUSES = new Set([408, 425, 429])

export function isDefinitiveMarketplaceActionError(error) {
  const status = Number(error?.response?.status)
  return (
    error?.marketplaceOutcome === "definitive_rejection" ||
    (Number.isInteger(status) &&
      status >= 400 &&
      status < 500 &&
      !AMBIGUOUS_MARKETPLACE_HTTP_STATUSES.has(status))
  )
}

function definitiveMarketplaceError(message, response = null) {
  const error = new Error(message)
  error.marketplaceOutcome = "definitive_rejection"
  if (response) error.response = response
  return error
}

function marketplaceHttpError(message, response) {
  const error = new Error(message)
  error.response = response
  if (isDefinitiveMarketplaceActionError(error)) {
    error.marketplaceOutcome = "definitive_rejection"
  }
  return error
}

function assertValidMarketplaceRequestKey(requestKey) {
  if (!isValidMarketplaceRequestKey(requestKey)) {
    throw new Error("Invalid marketplace request key")
  }
}

const POLLABLE_HTTP_OPERATION_STATUSES = new Set([
  "accepted_pending_remote",
  "retryable_error",
  "noop_already_pending",
  "current_in_progress",
  "current_incomplete",
  "sent_sync_success",
])

/**
 * Return raw marketplace response data only for successful HTTP responses.
 *
 * @param {{ data?: any, status?: number, __handled401?: boolean }} response
 * @returns {Record<string, any>|null}
 * @throws {Error} when the shared API layer converted auth failure into a resolved response.
 * @precondition `response` is the direct Axios response from `api.post(...)`.
 * @postcondition Handled `401` responses reject so action UIs cannot render success fallbacks.
 * @mutates none
 */
function marketplaceResponseData(response) {
  if (response?.__handled401 || response?.status === 401) {
    throw definitiveMarketplaceError(
      "Требуется повторный вход в редактор",
      response
    )
  }

  if (Number(response?.status || 200) >= 400) {
    throw marketplaceHttpError(
      response?.data?.error ||
        response?.data?.message ||
        "Marketplace action failed",
      response
    )
  }

  const data = response?.data ?? null

  if (
    data &&
    (data.ok === false ||
      data.result === 0 ||
      Boolean(data.error) ||
      (Array.isArray(data.errors) && data.errors.length > 0))
  ) {
    throw definitiveMarketplaceError(
      data.error ||
        data.errors?.join(", ") ||
        data.message ||
        "Marketplace action failed",
      response
    )
  }

  return data
}

function pollableHttpErrorData(error) {
  const response = error?.response
  const data = response?.data
  const operationStatus = data?.operation?.status

  if (
    Number(response?.status || 0) === 409 &&
    POLLABLE_HTTP_OPERATION_STATUSES.has(operationStatus)
  ) {
    return data
  }

  return null
}

/**
 * Preserve only a server-declared verification-required operation for this request key.
 * Other 502 responses stay ambiguous; this DTO never claims provider verification or completion.
 * @param {unknown} error
 * @param {string} requestKey
 * @returns {{ operationId: string, status: 'verification_required', pollable: true, verified: false, terminal: false }|null}
 * @mutates none
 */
function verificationRequiredOperation(error, requestKey) {
  const response = error?.response
  const data = response?.data
  const operation = data?.operation

  if (
    response?.status !== 502 ||
    data?.result !== 0 ||
    data?.status !== 0 ||
    !operation ||
    typeof operation !== "object" ||
    Array.isArray(operation) ||
    operation.operation_id !== requestKey ||
    operation.status !== "verification_required" ||
    operation.is_accepted !== false
  ) {
    return null
  }

  return {
    operationId: requestKey,
    status: "verification_required",
    pollable: true,
    verified: false,
    terminal: false,
  }
}

async function marketplaceActionRequest(request, requestKey) {
  try {
    return marketplaceResponseData(await request())
  } catch (error) {
    const operation = verificationRequiredOperation(error, requestKey)
    if (operation) error.marketplaceOperation = operation
    const pollableData = pollableHttpErrorData(error)
    if (pollableData) return pollableData
    throw error
  }
}

function marketplaceStatusData(response) {
  const data = marketplaceResponseData(response)
  const states = Array.isArray(data?.states) ? data.states : []
  const latest = states.length > 0 ? states[states.length - 1] : {}
  const pollableStatuses = new Set([
    "accepted_pending_remote",
    "retryable_error",
    "noop_already_pending",
    "current_in_progress",
    "current_incomplete",
    "sent_sync_success",
  ])
  const nextPending = states
    .filter(
      (state) =>
        pollableStatuses.has(String(state?.status || "")) && state?.retry_at
    )
    .sort(
      (left, right) =>
        new Date(left.retry_at).getTime() - new Date(right.retry_at).getTime()
    )[0]
  const statusAuthority = nextPending || latest

  return {
    marketplace: data?.marketplace,
    action: data?.action,
    operationId: data?.operation_id ?? null,
    productType: data?.product_type,
    productId: data?.local_id,
    status: data?.status ?? latest.status ?? "unknown",
    rawStatus: data?.status ?? latest.status ?? "unknown",
    retryAt: nextPending?.retry_at ?? null,
    states,
    meta:
      statusAuthority.meta &&
      typeof statusAuthority.meta === "object" &&
      !Array.isArray(statusAuthority.meta)
        ? { ...statusAuthority.meta }
        : {},
  }
}

/**
 * Create marketplace client for Editor-triggered marketplace actions.
 *
 * @returns {{ publishOzonProduct: (payload: { productType: string, productId: number|string, requestKey: string }) => Promise<Record<string, any>>, addOzonProductPhoto: (payload: { productType: string, productId: number|string, requestKey: string }) => Promise<Record<string, any>>, createWbProduct: (payload: { productType: string, productId: number|string, requestKey: string }) => Promise<Record<string, any>>, updateWbProduct: (payload: { productType: string, productId: number|string, requestKey: string }) => Promise<Record<string, any>>, addWbProductPhoto: (payload: { productType: string, productId: number|string, requestKey: string }) => Promise<Record<string, any>> }}
 * @precondition Caller passes a backend-owned marketplace action payload.
 * @postcondition Marketplace requests are sent only through canonical `/api/marketplace/*` seams.
 * @mutates none
 */
export function createMarketplaceClient() {
  return {
    /**
     * Resolve authoritative Ozon presence; other responses fail closed.
     * Only local rate-limit deferrals retain a valid retry deadline.
     *
     * @param {{ productType: string, productId: number|string }} query
     * @returns {Promise<{state: string, action: string|null, productId: number|null, reason: string, retryNotBefore?: string}>}
     */
    async ozonPresence(query) {
      const response = await api.get(ENDPOINTS.OZON_PRESENCE, {
        params: {
          product_type: query.productType,
          local_id: query.productId,
        },
      })
      const data = marketplaceResponseData(response)
      const presence =
        data?.presence && typeof data.presence === "object" ? data.presence : {}
      const state = String(presence.state ?? "").trim()
      const action = String(presence.action ?? "").trim()
      const numericProductId = Number(presence.product_id)
      const productId =
        Number.isSafeInteger(numericProductId) && numericProductId > 0
          ? numericProductId
          : null
      const validPresent =
        state === "present" && action === "update" && productId !== null
      const validAbsent =
        state === "absent" &&
        action === "create" &&
        productId === null &&
        (presence.product_id === null ||
          !Object.prototype.hasOwnProperty.call(presence, "product_id"))
      if (!validPresent && !validAbsent) {
        const result = {
          state: "unknown",
          action: null,
          productId: null,
          reason: presence.reason ?? "inconsistent_presence_response",
        }
        if (
          presence.reason === "local_rate_limit_deferred" &&
          typeof presence.retry_not_before === "string" &&
          Number.isFinite(Date.parse(presence.retry_not_before))
        ) {
          result.retryNotBefore = presence.retry_not_before
        }
        return result
      }

      return {
        state,
        action,
        productId,
        reason: presence.reason ?? "authoritative_presence_resolved",
      }
    },

    /**
     * Resolve the authoritative local WB listing presence for one Editor card.
     *
     * @param {{ productType: string, productId: number|string }} query
     * @returns {Promise<{state: string, action: string|null, productId: number|null, reason: string}>}
     * @postconditionAnything other than one authoritative present/absent projection resolves to fail-closed `unknown`.
     */
    async wbPresence(query) {
      const response = await api.get(ENDPOINTS.WB_PRESENCE, {
        params: {
          product_type: query.productType,
          local_id: query.productId,
        },
      })
      const data = marketplaceResponseData(response)
      const presence =
        data?.presence && typeof data.presence === "object" ? data.presence : {}
      const state = String(presence.state ?? "").trim()
      const action = String(presence.action ?? "").trim()
      const numericProductId = Number(presence.product_id)
      const productId =
        Number.isSafeInteger(numericProductId) && numericProductId > 0
          ? numericProductId
          : null
      const validPresent =
        state === "present" && action === "update" && productId !== null
      const validAbsent =
        state === "absent" &&
        action === "create" &&
        productId === null &&
        (presence.product_id === null ||
          !Object.prototype.hasOwnProperty.call(presence, "product_id"))
      if (!validPresent && !validAbsent) {
        return {
          state: "unknown",
          action: null,
          productId: null,
          reason: presence.reason ?? "inconsistent_presence_response",
        }
      }

      return {
        state,
        action,
        productId,
        reason: presence.reason ?? "authoritative_presence_resolved",
      }
    },

    async status(query) {
      const response = await api.get(ENDPOINTS.STATUS, {
        params: {
          marketplace: query.marketplace,
          action: query.action,
          product_type: query.productType,
          local_id: query.productId,
          operation_id: query.operationId,
        },
      })
      return marketplaceStatusData(response)
    },

    /**
     * Send one LP/CD product-card publish request to the backend Ozon action seam.
     *
     * @param {{ productType: string, productId: number|string, requestKey: string }} payload
     * @returns {Promise<Record<string, any>>}
     * @precondition `payload.productType` and `payload.productId` are already chosen by the caller's authoritative UI flow.
     * @postcondition Resolves with the raw backend response body so the caller can render truthful success/error feedback.
     * @mutates none
     */
    async publishOzonProduct(payload) {
      assertValidMarketplaceRequestKey(payload?.requestKey)
      return marketplaceActionRequest(
        () => api.post(ENDPOINTS.OZON_PUBLISH, payload),
        payload.requestKey
      )
    },

    /**
     * Send one LP product-card photo refresh request to the backend Ozon media seam.
     *
     * @param {{ productType: string, productId: number|string, requestKey: string }} payload
     * @returns {Promise<Record<string, any>>}
     * @precondition `payload.productType` and `payload.productId` are already chosen by the caller's authoritative UI flow.
     * @postcondition Resolves with the raw backend response body so the caller can render truthful success/error feedback.
     * @mutates none
     */
    async addOzonProductPhoto(payload) {
      assertValidMarketplaceRequestKey(payload?.requestKey)
      return marketplaceActionRequest(
        () => api.post(ENDPOINTS.OZON_PHOTO, payload),
        payload.requestKey
      )
    },

    /**
     * Send one LP WB create/publish request to the backend action seam.
     *
     * @param {{ productType: string, productId: number|string, requestKey: string }} payload
     * @returns {Promise<Record<string, any>>}
     */
    async createWbProduct(payload) {
      assertValidMarketplaceRequestKey(payload?.requestKey)
      return marketplaceActionRequest(
        () => api.post(ENDPOINTS.WB_CREATE, payload),
        payload.requestKey
      )
    },

    /**
     * Send one LP WB update request to the backend action seam.
     *
     * @param {{ productType: string, productId: number|string, requestKey: string }} payload
     * @returns {Promise<Record<string, any>>}
     */
    async updateWbProduct(payload) {
      assertValidMarketplaceRequestKey(payload?.requestKey)
      return marketplaceActionRequest(
        () => api.post(ENDPOINTS.WB_UPDATE, payload),
        payload.requestKey
      )
    },

    /**
     * Send one LP WB photo attach request to the backend action seam.
     *
     * @param {{ productType: string, productId: number|string, requestKey: string }} payload
     * @returns {Promise<Record<string, any>>}
     */
    async addWbProductPhoto(payload) {
      assertValidMarketplaceRequestKey(payload?.requestKey)
      return marketplaceActionRequest(
        () => api.post(ENDPOINTS.WB_PHOTO, payload),
        payload.requestKey
      )
    },
  }
}

export const marketplaceClient = createMarketplaceClient()
